/**
 * A campaign's daily ad log (Confluence OP/3623026689). PURE.
 *
 * The media owner uploads one file per day, named YYYYMMDD.csv, with no
 * header and five columns: TabID (numeric), run date (YYYY-MM-DD, the file's
 * own date), air time (HH:MM:SS), duration (seconds) and creative #. A line
 * is refused, by row, when its TabID is not one of the campaign's placements,
 * its run date is not the file's, or its time or duration does not read.
 */
export interface AdLogLine {
    tabId: string;
    runDate: string;
    airTime: string;
    duration: number;
    creative: string;
}

export interface ParsedAdLog {
    /** The file's date, from its name; null when the name is not YYYYMMDD.csv. */
    runDate: string | null;
    lines: AdLogLine[];
    refusals: string[];
}

/** "20190515.csv" → "2019-05-15"; null for any other name. */
export function adLogDateOf(fileName: string): string | null {
    const m = fileName
        .trim()
        .split(/[\\/]/)
        .pop()!
        .match(/^(\d{4})(\d{2})(\d{2})\.csv$/i);
    if (!m) return null;
    const iso = `${m[1]}-${m[2]}-${m[3]}`;
    const d = new Date(`${iso}T00:00:00Z`);
    return Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== iso ? null : iso;
}

/** True when a file reads as an ad log: its name is a date and its first lines have the five columns. */
export function looksLikeAdLog(fileName: string, rows: string[][]): boolean {
    if (!adLogDateOf(fileName)) return false;
    const sample = rows.filter((r) => r.some((c) => String(c ?? '').trim())).slice(0, 10);
    return (
        sample.length > 0 &&
        sample.every(
            (r) =>
                /^\d+$/.test(String(r[0] ?? '').trim()) &&
                /^\d{4}-\d{2}-\d{2}$/.test(String(r[1] ?? '').trim()),
        )
    );
}

const TIME = /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/;

export function parseAdLog(
    fileName: string,
    rows: string[][],
    campaign: { tabIds?: Iterable<string>; flightStart?: string; flightEnd?: string } = {},
): ParsedAdLog {
    const runDate = adLogDateOf(fileName);
    if (!runDate)
        return {
            runDate: null,
            lines: [],
            refusals: [
                'The file must be named by its date, YYYYMMDD.csv (for example 20190515.csv).',
            ],
        };
    const refusals: string[] = [];
    if (campaign.flightStart && runDate < campaign.flightStart)
        refusals.push(`${fileName} is before the flight starts (${campaign.flightStart}).`);
    if (campaign.flightEnd && runDate > campaign.flightEnd)
        refusals.push(`${fileName} is after the flight ends (${campaign.flightEnd}).`);
    if (refusals.length) return { runDate, lines: [], refusals };
    const known = campaign.tabIds ? new Set(campaign.tabIds) : null;
    const lines: AdLogLine[] = [];
    rows.forEach((row, i) => {
        const cell = (c: number) => String(row[c] ?? '').trim();
        if (row.every((c) => !String(c ?? '').trim())) return;
        const n = i + 1;
        const [tabId, date, airTime, duration, creative] = [0, 1, 2, 3, 4].map(cell);
        if (!/^\d+$/.test(tabId))
            return void refusals.push(`Row ${n}: the TabID must be a number.`);
        if (known && !known.has(tabId))
            return void refusals.push(
                `Row ${n}: TabID ${tabId} is not one of the campaign's placements.`,
            );
        if (date !== runDate)
            return void refusals.push(
                `Row ${n}: the run date ${date || '(blank)'} is not the file's date, ${runDate}.`,
            );
        if (!TIME.test(airTime))
            return void refusals.push(`Row ${n}: the air time must be HH:MM:SS.`);
        const seconds = Number(duration);
        if (!Number.isFinite(seconds) || seconds <= 0)
            return void refusals.push(`Row ${n}: the duration must be a number of seconds.`);
        lines.push({ tabId, runDate, airTime, duration: seconds, creative: creative.slice(0, 40) });
    });
    return { runDate, lines, refusals };
}

/** Every day of a flight that has no ad log. */
export function missingDays(
    flightStart: string,
    flightEnd: string,
    have: Iterable<string>,
    through?: string,
): string[] {
    const got = new Set(have);
    const out: string[] = [];
    const end = through && through < flightEnd ? through : flightEnd;
    for (
        let d = new Date(`${flightStart}T00:00:00Z`);
        d.toISOString().slice(0, 10) <= end && out.length < 400;
        d = new Date(d.getTime() + 86_400_000)
    ) {
        const day = d.toISOString().slice(0, 10);
        if (!got.has(day)) out.push(day);
    }
    return out;
}
